import React, { useState } from 'react';
import { PrimaryButton, Card, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';

interface GuessNumberGameProps {
  appName: string;
  outletId: number;
  onClose?: () => void;
}

const GuessNumberGame: React.FC<GuessNumberGameProps> = ({ appName, outletId, onClose }) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'GUESS_NUMBER');
  const [guess, setGuess] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [hiddenNumber, setHiddenNumber] = useState<number | null>(null);
  const [rewardLabel, setRewardLabel] = useState<string | null>(null);
  const [redemptionCode, setRedemptionCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submitGuess() {
    if (guess === null || submitting || !eligible || revealed) return;
    setSubmitting(true);
    try {
      const res = await gamesService.spin(
        appName,
        'GUESS_NUMBER',
        gamesService.dailyIdempotencyKey(outletId, 'GUESS_NUMBER')
      );
      setRewardLabel(res.resultSegment);
      setRedemptionCode(res.redemptionCode);
      const match = res.resultSegment.match(/hidden:(\d+)/);
      setHiddenNumber(match ? parseInt(match[1], 10) : null);
      setRevealed(true);
    } catch {
      setRevealed(true);
      setRewardLabel('Come back tomorrow for another guess.');
    }
    setSubmitting(false);
  }

  const locked = !loading && !eligible;

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={<>Guess the <span style={{ color: '#D9472B' }}>Chef's Number</span></>}
        subtitle="Pick 1-20 - Closest guess wins a discount"
        onClose={onClose}
      />
      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {locked && <GameLockedCard eligibility={eligibility} />}

        {!loading && eligible && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 8 }}>
              {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => !revealed && setGuess(n)}
                  disabled={revealed}
                  style={{
                    padding: '10px 0',
                    borderRadius: 10,
                    border: `1px solid ${guess === n ? 'var(--primary, #F5C21A)' : 'var(--border-color, #E5E2D8)'}`,
                    background: guess === n ? 'var(--primary, #F5C21A)' : '#FFFFFF',
                    color: 'var(--text-primary, #1F2428)',
                    fontWeight: guess === n ? 700 : 500,
                    fontSize: 13,
                    cursor: revealed ? 'default' : 'pointer',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>

            <div style={{ height: 18 }} />

            {!revealed ? (
              <PrimaryButton onClick={submitGuess} disabled={guess === null || submitting}>
                {submitting ? 'CHECKING…' : 'LOCK MY GUESS'}
              </PrimaryButton>
            ) : (
              <Card style={{ textAlign: 'center' }}>
                {hiddenNumber !== null && (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary, #6B5E3E)' }}>
                    The chef's number was <b>{hiddenNumber}</b> - you guessed <b>{guess}</b>
                  </p>
                )}
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 20,
                    color: redemptionCode ? 'var(--primary-dark, #D4A416)' : 'var(--text-secondary, #6B5E3E)',
                    marginTop: 8,
                  }}
                >
                  {rewardLabel}
                </div>
                {redemptionCode && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border-color, #E5E2D8)' }}>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)' }}>CODE</div>
                    <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: 2 }}>{redemptionCode}</div>
                  </div>
                )}
              </Card>
            )}

            <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted, #9B8F6E)', marginTop: 12 }}>
              One guess per customer / day
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default GuessNumberGame;
